import { useState } from "react";
import { Image, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Builder-chrome control that rebuilds a blob-copy template's WHOLE page copy
// (quiz / listicle / advertorial / coffee-shop). Rendered above the preview
// surface (not inside the page canvas) so it reads as an editor control rather
// than page content — this placement is a user-preference rule (see replit.md).
// Optional free-text feedback steers the rewrite; AI imagery is preserved
// server-side.
export function LpRegenerateCard({
  idPrefix,
  busy,
  onRegenerate,
}: {
  idPrefix: string;
  busy: boolean;
  onRegenerate: (feedback: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  return (
    <Card className="border-border/40" data-testid={`${idPrefix}-regenerate-bar`}>
      <div className="p-3">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Regenerate page</div>
              <p className="text-xs text-muted-foreground">
                Rewrites the whole page&apos;s copy from this page&apos;s brand,
                persona, product and angle. Images are kept; your text edits
                will be replaced.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setOpen((o) => !o)}
              disabled={busy}
              data-testid={`button-${idPrefix}-regenerate-feedback-toggle`}
            >
              {open ? "Hide feedback" : "Add feedback"}
            </Button>
            <Button
              size="sm"
              className="h-8"
              onClick={() => {
                onRegenerate(feedback.trim());
                setOpen(false);
              }}
              disabled={busy}
              data-testid={`button-${idPrefix}-regenerate`}
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              )}
              {busy ? "Regenerating…" : "Regenerate"}
            </Button>
          </div>
        </div>
        {open && (
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="Optional: tell the AI what to change (tone, offer, angle…)"
            disabled={busy}
            className="mt-3"
            data-testid={`input-${idPrefix}-regenerate-feedback`}
          />
        )}
      </div>
    </Card>
  );
}

export function LpStudioRegenerateCard({
  copyStatus,
  copyError,
  imageStatus,
  imageError,
  sectionBlockedError,
  conflictingBusy = false,
  onRegenerateCopy,
  onRegenerateImages,
}: {
  copyStatus?: string | null;
  copyError?: string | null;
  imageStatus?: string | null;
  imageError?: string | null;
  sectionBlockedError?: string | null;
  conflictingBusy?: boolean;
  onRegenerateCopy: () => void;
  onRegenerateImages: () => void;
}) {
  const [confirm, setConfirm] = useState<"copy" | "images" | null>(null);
  const busy =
    conflictingBusy ||
    copyStatus === "pending" ||
    imageStatus === "pending" ||
    copyStatus === "blocked" ||
    imageStatus === "blocked";
  return (
    <>
      <Card className="border-border/40" data-testid="studio-regenerate-bar">
        <div className="p-3 space-y-3">
          <div>
            <div className="text-sm font-semibold">Regenerate page</div>
            <p className="text-xs text-muted-foreground">
              Replace copy or generated imagery independently. Studio hero and
              section lifestyle slots regenerate; the hero product photo,
              uploads, product gallery and brand logo stay unchanged.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="h-8"
              disabled={busy}
              onClick={() => setConfirm("copy")}
              data-testid="button-studio-regenerate-copy"
            >
              {copyStatus === "pending" ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              )}
              {copyStatus === "pending" ? "Regenerating copy…" : "Regenerate all copy"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={busy}
              onClick={() => setConfirm("images")}
              data-testid="button-studio-regenerate-images"
            >
              {imageStatus === "pending" ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Image className="h-3.5 w-3.5 mr-1.5" />
              )}
              {imageStatus === "pending" ? "Regenerating images…" : "Regenerate all images"}
            </Button>
          </div>
          {(copyStatus === "failed" || copyStatus === "blocked") && copyError && (
            <p className="text-xs text-destructive" data-testid="text-studio-copy-error">
              Copy: {copyError}
            </p>
          )}
          {(imageStatus === "failed" || imageStatus === "blocked") && imageError && (
            <p className="text-xs text-destructive" data-testid="text-studio-images-error">
              Images: {imageError}
            </p>
          )}
          {sectionBlockedError && (
            <p className="text-xs text-destructive" data-testid="text-studio-section-blocked">
              Section: {sectionBlockedError}
            </p>
          )}
        </div>
      </Card>
      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent data-testid={`dialog-studio-regenerate-${confirm ?? "closed"}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "copy" ? "Replace all page copy?" : "Replace all AI images?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "copy"
                ? "This replaces copy across the selected Studio template, including manual text edits. Images, uploaded media and layout are preserved."
                : "This replaces every generated lifestyle image used by the Studio hero or sections. The hero product image is real product media, not a generated slot; copy, layout, product photos, uploaded overrides and the brand logo are preserved. An uploaded override may continue to conceal its newly generated underlying image."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-studio-regenerate-confirm"
              onClick={() => {
                if (confirm === "copy") onRegenerateCopy();
                if (confirm === "images") onRegenerateImages();
                setConfirm(null);
              }}
            >
              Replace {confirm === "copy" ? "copy" : "AI images"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function LpStudioRevisionCard({
  status,
  error,
  conflictingBusy = false,
  onSubmit,
}: {
  status?: string | null;
  error?: string | null;
  conflictingBusy?: boolean;
  onSubmit: (instruction: string) => void;
}) {
  const [instruction, setInstruction] = useState("");
  const busy = conflictingBusy || status === "revising";
  const canSubmit = instruction.trim().length > 0 && !busy;
  return (
    <Card className="border-border/40" data-testid="studio-revision-bar">
      <div className="p-3 space-y-3">
        <div>
          <div className="text-sm font-semibold">Edit page design</div>
          <p className="text-xs text-muted-foreground">
            Describe a visual change to this generated page. Structured copy,
            images, sections, and bindings stay protected.
          </p>
        </div>
        <Textarea
          value={instruction}
          onChange={(event) => setInstruction(event.target.value.slice(0, 8_000))}
          rows={3}
          maxLength={8_000}
          placeholder="For example: make the hero feel warmer with a softer visual hierarchy…"
          disabled={busy}
          data-testid="input-studio-revision-instruction"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            {instruction.length}/8,000
          </span>
          <Button
            size="sm"
            className="h-8"
            disabled={!canSubmit}
            onClick={() => {
              onSubmit(instruction.trim());
              setInstruction("");
            }}
            data-testid="button-studio-revision-submit"
          >
            {status === "revising" ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            )}
            {status === "revising" ? "Updating design…" : "Update design"}
          </Button>
        </div>
        {error && (
          <p className="text-xs text-destructive" data-testid="text-studio-revision-error">
            Design update: {error}
          </p>
        )}
        {status === "ready" && !error && (
          <p className="text-xs text-emerald-600" data-testid="text-studio-revision-success">
            Design updated. Your draft is ready to edit.
          </p>
        )}
      </div>
    </Card>
  );
}